import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle, Quote } from 'lucide-react';
import SEO from '../components/SEO';
import LazyImage from '../components/LazyImage';

const CaseStudyKatam = () => {
  const siteUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://orboroi.com';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Katam Technologies Forestry AI - Aerial Image Annotation Case Study",
    "description": "How Orboroi delivered 10,000+ aerial images with 99%+ accuracy for Katam Technologies' AI-powered forestry management and tree detection platform.",
    "image": "https://images.unsplash.com/photo-1728932828917-db6f16a257dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxhZXJpYWwlMjBmb3Jlc3RyeSUyMGRyb25lJTIwdHJlZSUyMGRldGVjdGlvbiUyMG1hcHBpbmclMjBsaWRhcnxlbnwwfHx8fDE3NzQyOTg1OTJ8MA&ixlib=rb-4.1.0&q=85",
    "datePublished": "2024-03-05",
    "dateModified": "2024-03-05",
    "author": {
      "@type": "Organization",
      "name": "Orboroi"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Orboroi",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/case-study-katam`
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Case Studies", url: "/case-studies" },
    { name: "Katam Technologies", url: "/case-study-katam" }
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO
        title="Katam Technologies Forestry AI Case Study"
        description="How Orboroi delivered 10,000+ aerial images with 99%+ accuracy for Katam's AI-powered forestry management and automated tree detection platform."
        canonical="/case-study-katam"
        type="article"
        author="Orboroi Team"
        datePublished="2024-03-05"
        structuredData={structuredData}
        breadcrumbs={breadcrumbs}
        image="https://images.unsplash.com/photo-1728932828917-db6f16a257dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxhZXJpYWwlMjBmb3Jlc3RyeSUyMGRyb25lJTIwdHJlZSUyMGRldGVjdGlvbiUyMG1hcHBpbmclMjBsaWRhcnxlbnwwfHx8fDE3NzQyOTg1OTJ8MA&ixlib=rb-4.1.0&q=85"
      />
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Link to="/" className="hover:text-[#379F7F]">Home</Link>
          <span>→</span>
          <Link to="/case-studies" className="hover:text-[#379F7F]">Case Studies</Link>
          <span>→</span>
          <span className="text-white">Katam Technologies</span>
        </div>
      </div>

      <section className="py-12 bg-gradient-to-b from-green-900 to-emerald-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Case Study — <span className="text-[#379F7F]">Katam Technologies</span>
            </h1>
            <p className="text-xl text-gray-300 mb-2">Forestry AI</p>
            <p className="text-gray-400">Data Preparation & Custom Solutions</p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            
            <div>
              <h2 className="text-3xl font-bold text-white mb-4">The Challenge</h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                Katam needed high-quality annotated data to train computer vision models for forestry optimization, including detection of different tree species. The challenge was to deliver consistent, precise annotations at scale.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-white mb-4">Our Solution</h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Annotated 10,000+ images of trees with bounding boxes for accurate detection model training. Deployed top data annotators achieving 99%+ accuracy. Tailored polygon annotation training for precision. Conducted regular progress reviews, rigorous QA checks, and provided actionable insights for continuous model improvement.
              </p>
              <div className="case-study-image-container">
                <LazyImage 
                  src="https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxmb3Jlc3QlMjB0cmVlcyUyMGRldGVjdGlvbiUyMGNvbXB1dGVyJTIwdmlzaW9ufGVufDB8fHx8MTc3NDI5ODU5NHww&ixlib=rb-4.1.0&q=85"
                  alt="Forestry AI showing Katam Technologies' automated tree detection and aerial mapping platform"
                  className="case-study-image"
                />
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Results</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">99%+ annotation accuracy for detection model training</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Improved model performance for forestry optimization</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Scalable annotation process established</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">10,000+ images annotated with bounding boxes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Regular progress reviews and QA checks implemented</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Continuous model improvement through actionable insights</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <Quote className="w-12 h-12 text-[#379F7F] mb-4" />
              <p className="text-xl text-gray-200 italic mb-6 leading-relaxed">
                "The quality of annotations provided by Orboroi significantly improved our model's performance. Their attention to detail and commitment to accuracy made all the difference."
              </p>
              <div className="border-t border-gray-700 pt-4">
                <p className="text-white font-semibold">Katam Technologies Team</p>
                <p className="text-gray-400">Forestry AI Division</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 mt-12 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                Ready to Scale Your AI with Data Certainty?
              </h3>
              <p className="text-gray-300 mb-6">
                Let's discuss how Orboroi can help accelerate your AI development.
              </p>
              <Link to="/contact">
                <Button className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold">
                  Get in Touch
                </Button>
              </Link>
            </div>

            <div className="pt-8">
              <Link to="/case-studies" className="text-[#379F7F] hover:underline inline-flex items-center">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to All Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyKatam;
